import React, { useState } from 'react';

/**
 * PendingLogsPanel
 *
 * The collapsed/expanded strip of "Not an achievement yet?" placeholders.
 * Deliberately plain: no status, no due date, no priority — see
 * design-system/Terminology.md. Collapsed by default so it reads as a
 * quiet counter, not a task list competing with the achievements below it.
 */
export default function PendingLogsPanel({ pendingLogs = [], onCapture, onDismiss }) {
  const [expanded, setExpanded] = useState(false);

  if (pendingLogs.length === 0) return null;

  const formatAge = (createdAt) => {
    const days = Math.max(0, Math.round((Date.now() - new Date(createdAt).getTime()) / 86400000));
    if (days === 0) return 'Added today';
    if (days === 1) return 'Added 1 day ago';
    if (days < 14) return `Added ${days} days ago`;
    const weeks = Math.round(days / 7);
    return `Added ${weeks} week${weeks === 1 ? '' : 's'} ago`;
  };

  return (
    <div className="mx-4 md:mx-8 mb-3 rounded-xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 bg-stone-50 px-4 py-2.5 hover:bg-stone-100 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l2 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-gray-600">
            <span className="font-semibold text-gray-900">
              {pendingLogs.length} thing{pendingLogs.length === 1 ? '' : 's'}
            </span>{' '}
            you're tracking — not achievements yet
          </p>
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="border-t border-gray-200">
          {pendingLogs.map((log, i) => (
            <div
              key={log.id}
              className={`flex items-center justify-between gap-3 px-4 py-3 ${i < pendingLogs.length - 1 ? 'border-b border-gray-100' : ''}`}
            >
              <div className="min-w-0">
                <p className="text-sm text-gray-800 truncate">{log.note}</p>
                <p className="text-xs text-gray-400 mt-0.5">{formatAge(log.created_at)}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => onCapture(log)}
                  className="text-sm font-semibold text-gray-900 hover:underline"
                >
                  Capture it →
                </button>
                <button
                  type="button"
                  onClick={() => onDismiss(log.id)}
                  className="text-gray-300 hover:text-gray-500"
                  title="Dismiss"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
